import { useQuery } from "@tanstack/react-query";
import { useEffect } from "react";
import { useAnalytics } from "../contexts/analytics/hooks";
import { backendService } from "../services/backend.service";

/** A building's departments change only when a department is created, so a minute-old list is fine. */
const FACILITY_DEPARTMENTS_STALE_MS = 60_000;

/** Every department's facility listing. Invalidated after a department is created, which may have joined one. */
export const DEPARTMENT_FACILITY_QUERY_KEY = ["department-facility"] as const;

/**
 * The department's facility and every department in it, so settings saved for the whole building can name
 * the departments a save changes. `enabled` is false on the Create Department form, which has no department yet.
 */
export function useDepartmentFacility({ departmentId, enabled }: { departmentId: string | undefined; enabled: boolean }) {
	const { trackError } = useAnalytics();

	const query = useQuery({
		queryKey: [...DEPARTMENT_FACILITY_QUERY_KEY, departmentId],
		queryFn: async () => {
			if (departmentId === undefined) throw new Error("A department is required to load its facility");
			return (await backendService.getDepartmentFacility(departmentId)).data;
		},
		enabled: enabled && departmentId !== undefined,
		staleTime: FACILITY_DEPARTMENTS_STALE_MS,
	});

	useEffect(() => {
		if (query.error) trackError("dept_config_department_facility_fetch_error", { department_id: departmentId, error_message: query.error.message });
	}, [query.error, departmentId, trackError]);

	return query;
}
